/**
 * Natural-language quick-add parsing with chrono-node for flexible dates.
 * Supports: today, tomorrow, next Friday, in 3 days, p1/p2/p3, daily/weekly/monthly
 */

import * as chrono from "chrono-node";

const PRIORITY_MAP = { p1: "high", p2: "medium", p3: "low", p4: "low" };

function toDateInputValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function stripRegexDate(text) {
    let duePreset = null;
    let customDueDate = null;

    if (/\bday after tomorrow\b/i.test(text)) {
        const d = new Date();
        d.setDate(d.getDate() + 2);
        duePreset = "custom";
        customDueDate = toDateInputValue(d);
        text = text.replace(/\bday after tomorrow\b/i, " ").replace(/\s+/g, " ").trim();
    } else if (/\btomorrow\b/i.test(text)) {
        duePreset = "tomorrow";
        text = text.replace(/\btomorrow\b/i, " ").replace(/\s+/g, " ").trim();
    } else if (/\btoday\b/i.test(text)) {
        duePreset = "today";
        text = text.replace(/\btoday\b/i, " ").replace(/\s+/g, " ").trim();
    } else if (/\bnext week\b/i.test(text)) {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        duePreset = "custom";
        customDueDate = toDateInputValue(d);
        text = text.replace(/\bnext week\b/i, " ").replace(/\s+/g, " ").trim();
    } else {
        const m = text.match(/\bin (\d+) days?\b/i);
        if (m) {
            const d = new Date();
            d.setDate(d.getDate() + parseInt(m[1], 10));
            duePreset = "custom";
            customDueDate = toDateInputValue(d);
            text = text.replace(m[0], " ").replace(/\s+/g, " ").trim();
        }
    }

    return { text, duePreset, customDueDate };
}

function stripChronoDate(text) {
    const results = chrono.parse(text, new Date(), { forwardDate: true });
    if (!results.length) {
        return { text, duePreset: null, customDueDate: null };
    }

    const match = results[0];
    const d = match.start.date();
    const title =
        (text.slice(0, match.index) + text.slice(match.index + match.text.length))
            .replace(/\s+/g, " ")
            .trim();

    return {
        text: title,
        duePreset: "custom",
        customDueDate: toDateInputValue(d),
    };
}

export function parseQuickAddInput(raw) {
    let text = raw.trim();
    let priority = "medium";
    let duePreset = "today";
    let customDueDate = null;
    let repetition = "none";

    let m = text.match(/\b(p[1-4])\b/i);
    if (m) {
        priority = PRIORITY_MAP[m[1].toLowerCase()] || "medium";
        text = text.replace(m[0], " ").replace(/\s+/g, " ").trim();
    } else {
        m = text.match(/\!(high|medium|low)\b/i);
        if (m) {
            priority = m[1].toLowerCase();
            text = text.replace(m[0], " ").replace(/\s+/g, " ").trim();
        }
    }

    if (/\b(every day|daily)\b/i.test(text)) {
        repetition = "daily";
        text = text.replace(/\b(every day|daily)\b/i, " ").replace(/\s+/g, " ").trim();
    } else if (/\b(every week|weekly)\b/i.test(text)) {
        repetition = "weekly";
        text = text.replace(/\b(every week|weekly)\b/i, " ").replace(/\s+/g, " ").trim();
    } else if (/\b(every month|monthly)\b/i.test(text)) {
        repetition = "monthly";
        text = text.replace(/\b(every month|monthly)\b/i, " ").replace(/\s+/g, " ").trim();
    }

    const chronoResult = stripChronoDate(text);
    if (chronoResult.customDueDate) {
        text = chronoResult.text;
        duePreset = chronoResult.duePreset;
        customDueDate = chronoResult.customDueDate;
    } else {
        const regexResult = stripRegexDate(text);
        text = regexResult.text;
        if (regexResult.duePreset) {
            duePreset = regexResult.duePreset;
            customDueDate = regexResult.customDueDate;
        }
    }

    return {
        title: text,
        priority,
        duePreset,
        customDueDate,
        repetition,
        hadHints:
            raw.trim() !== text ||
            priority !== "medium" ||
            duePreset !== "today" ||
            repetition !== "none",
    };
}

export function quickAddHintText() {
    return 'Try: "Restock bar next Friday p1" or "Team sync every week"';
}
